import React, { useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Chip,
    CircularProgress,
    Alert,
    Avatar,
    Grid,
    Button,
    Tooltip,
} from '@mui/material';
import { MdRefresh } from 'react-icons/md';
import { useTheme } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import ReusableTable from '../../components/common/Table';
import TabFilter from '../../components/common/TabFilter';
import { usePayouts, getPayoutStats } from '../../hooks/UsePayouts';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import api from '../../config/interceptor';
import { showSnackbar } from '../../redux/slice/AppSlice';
import SearchField from '../../components/common/SearchFields';
import { formatPrice } from '../../utils/mappingData';


const PayoutHistory = () => {
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState('all');
    const dispatch = useDispatch();
    const [errorApi, setErrorApi] = useState(null);
    const [loadingApi, setLoadingApi] = React.useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [confirmState, setConfirmState] = useState({
        open: false,
        artist: null,
        onConfirm: () => { },
        cancelText: "",
        confirmText: "",
        message: "",
        variant: "",
        title: "",
    });

    // Use the custom hook to fetch payouts
    const { payouts, loading, error, refetch } = usePayouts({
        realtime: false,
        typeTransfer: 'offline',
        status: null, // Get all statuses
    });


    // Get statistics
    const stats = getPayoutStats(payouts);

    // Tabs for filtering
    const tabs = [
        { label: 'All', value: 'all' },
        { label: 'Paid', value: 'paid' },
        // { label: 'Pending', value: 'pending' },
        { label: 'Failed', value: 'failed' },
    ];

    // Columns for table — extra cols when viewing Failed tab
    const columns = useMemo(() => {
        const base = [
            { id: 'recipient', label: 'RECIPIENT', width: '20%', sortable: true },
            { id: 'amount',    label: 'AMOUNT',    width: '15%', sortable: true },
            { id: 'status',    label: 'STATUS',    width: '15%', sortable: true },
            { id: 'date',      label: 'DATE',      width: '20%', sortable: true },
        ];
        if (activeTab === 'failed') {
            return [
                ...base,
                { id: 'reason',  label: 'REASON',  width: '20%', sortable: false },
                { id: 'actions', label: 'ACTIONS', width: '10%', sortable: false },
            ];
        }
        return base;
    }, [activeTab]);

    // Format date
    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';

        try {
            if (timestamp.toDate) {
                return timestamp.toDate().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                });
            }
            if (timestamp instanceof Date) {
                return timestamp.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                });
            }
            return 'N/A';
        } catch (error) {
            return 'N/A';
        }
    };

    // Get initials
    const getInitials = (name) => {
        if (!name) return 'U';
        const parts = name.split(' ');
        return parts.map(p => p.charAt(0).toUpperCase()).join('').slice(0, 2);
    };

    // Get status color
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'paid':
            case 'succeeded':
                return {
                    bg: 'rgba(76, 175, 80, 0.15)',
                    color: theme.palette.success.main,
                };
            case 'pending':
                return {
                    bg: 'rgba(254, 203, 0, 0.15)',
                    color: theme.palette.primary.main,
                };
            case 'failed':
            case 'canceled':
                return {
                    bg: 'rgba(244, 67, 54, 0.15)',
                    color: theme.palette.error.main,
                };
            default:
                return {
                    bg: 'rgba(255, 255, 255, 0.1)',
                    color: theme.palette.text.secondary,
                };
        }
    };

    // Filter payouts based on active tab
    const filteredPayouts = useMemo(() => {
        let data = payouts.filter((payout) => {
            if (activeTab === 'all') return true;
            return payout.status?.toLowerCase() === activeTab;
        });

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            data = data.filter((payout) => {
                const name = (payout.recipientName || payout.artist_name || '').toLowerCase();
                const email = (payout.artist_email || '').toLowerCase();
                return name.includes(q) || email.includes(q);
            });
        }

        return data;
    }, [payouts, activeTab, searchQuery]);

    const handleConfirmClose = () => {
        setConfirmState({ open: false, artist: null });
        setErrorApi(null);
    };


    const onConfirm = async () => {
        try {
            setLoadingApi(true);
            // Call API to cancel payout
            const response = await api.post(`${confirmState?.endPointCall}`, {
                payout_id: confirmState?.artist?.payoutId,
                artist_id: confirmState?.artist?.artist_uuid,
            });
            setConfirmState({ open: false, artist: null });
            if (response?.data?.isSuccess && response?.status === 200) {
                // Refresh the payout list
                dispatch(showSnackbar({
                    message: confirmState?.SuccessMessage,
                    severity: 'success',
                }));
            }
            refetch();
        } catch (error) {
            console.error("Error cancelling payout: ", error);
            let errorMessage = 'Failed to process payout. Please try again.';
            if (error.response?.data?.message?.msg) {
                errorMessage = error.response.data.message.msg;
            }
            setErrorApi(errorMessage);
            dispatch(showSnackbar({
                message: errorMessage,
                severity: 'error',
            }));
        } finally {
            setLoadingApi(false);
        }
    }

    const handleRetry = (payout) => {
        if(payout?.is_retried){
            // in this i need show to user this make it before 
            setConfirmState({
                open: true,
                artist: payout,
                message: "You make retry befor for this faild Trasaction cant make it agine ",
                cancelText: 'Cancel',
                variant: 'warning',
            })
            return;
        }
        setConfirmState({
            open: true,
            artist: payout,
            title: 'Retry Payout',
            message: `Retry the failed payout of ${formatPrice(payout.amount || payout.totalTransfer)} for ${payout.recipientName || payout.artist_name || 'this artist'}?`,
            confirmText: 'Retry',
            cancelText: 'Cancel',
            variant: 'warning',
            endPointCall: '/payout/stripe/retry',
            SuccessMessage: 'Payout retry initiated successfully.',
        });
    };

    // Map to table data
    const tableData = filteredPayouts.map((payout) => {
        const statusColors = getStatusColor(payout.status);

        return {
            recipient: (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                        src={payout.artist_profile_picture || ''}
                        sx={{
                            width: 36,
                            height: 36,
                            bgcolor: theme.palette.primary.main,
                            color: theme.palette.blackColor.black1,
                            fontSize: '0.75rem',
                            fontWeight: 700,
                        }}
                    >
                        {getInitials(payout.recipientName || payout.artist_name)}
                    </Avatar>
                    <Box>
                        <Typography
                            sx={{
                                color: theme.palette.text.primary,
                                fontWeight: 500,
                                fontSize: '0.875rem',
                            }}
                        >
                            {payout.recipientName || payout.artist_name || 'Unknown'}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ color: theme.palette.text.secondary }}
                        >
                            {payout.artist_email || 'N/A'}
                        </Typography>
                    </Box>
                </Box>
            ),
            amount: (
                <Typography
                    sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        fontSize: '0.9rem',
                    }}
                >
                    {formatPrice(payout.amount)}
                </Typography>
            ),
            _sort_amount: parseFloat(payout.amount || 0),
            status: (
                <Chip
                    label={payout.status || 'Unknown'}
                    sx={{
                        backgroundColor: statusColors.bg,
                        color: statusColors.color,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        textTransform: 'capitalize',
                    }}
                />
            ),
            date: (
                <Typography
                    sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.8rem',
                    }}
                >
                    {formatDate(payout.processed_at || payout.created_at)}
                </Typography>
            ),
            _sort_date: (payout.processed_at || payout.created_at)?.toDate?.()?.getTime() ?? 0,
            ...(activeTab === 'failed' && {
                reason: (
                    <Typography
                        sx={{
                            color: payout.reason
                                ? theme.palette.error.main
                                : theme.palette.text.secondary,
                            fontSize: '0.8rem',
                            fontStyle: payout.reason ? 'normal' : 'italic',
                        }}
                    >
                        {payout.reason || 'No reason provided'}
                    </Typography>
                ),
                actions: (
                    <Tooltip title="Retry Payout" arrow>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<MdRefresh size={15} />}
                            onClick={(e) => { e.stopPropagation(); handleRetry(payout); }}
                            sx={{
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.blackColor.black1,
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                px: 2,
                                py: 0.6,
                                borderRadius: 2,
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                },
                            }}
                        >
                            Retry
                        </Button>
                    </Tooltip>
                ),
            }),
        };
    });

    // Loading state
    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '50vh',
                }}
            >
                <CircularProgress sx={{ color: theme.palette.primary.main }} />
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Box sx={{ width: '100%', p: 3 }}>
                <Alert severity="error">
                    Error loading data: {error}
                    <br />
                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                        Make sure Firestore security rules allow reading the Payouts subcollection.
                    </Typography>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Header */}
            <Box>
                <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: theme.palette.text.primary }}
                >
                    Payout History
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                    View all artist payout transactions
                </Typography>
            </Box>

            {/* Summary Cards */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {[
                    {
                        label: 'Total Payouts',
                        value: stats.totalPayouts,
                        color: theme.palette.primary.main,
                        bg: 'rgba(254, 203, 0, 0.1)',
                    },
                    {
                        label: 'Paid',
                        value: stats.paidPayouts,
                        color: theme.palette.success.main,
                        bg: 'rgba(76, 175, 80, 0.1)',
                    },
                    {
                        label: 'Total Amount',
                        value: `$${stats.totalAmount}`,
                        color: theme.palette.info.main,
                        bg: 'rgba(33, 150, 243, 0.1)',
                    },
                ].map((card, i) => (
                    <Box
                        key={i}
                        sx={{
                            flex: '1 1 180px',
                            backgroundColor: card.bg,
                            border: `1px solid ${card.color}30`,
                            borderRadius: 3,
                            p: 2.5,
                            textAlign: 'center',
                        }}
                    >
                        <Typography variant="h5" sx={{ color: card.color, fontWeight: 700 }}>
                            {card.value}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#888', fontWeight: 500 }}>
                            {card.label}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <Grid container spacing={2}>

                <Grid size={{ xs: 12, lg: 6 }}>
                    {/* ✅ Search Field with proper props */}
                    <SearchField
                        searchQuery={searchQuery}
                        handleSearchChange={(value) => {
                            if (typeof value === 'string') setSearchQuery(value);
                            else if (value?.target?.value !== undefined) setSearchQuery(value.target.value);
                        }}
                        placeholder="Search users by username or email..."
                    />
                </Grid>

                {/* Tab Filter */}
                <Grid size={{ xs: 12, lg: 6 }}>
                    <TabFilter
                        tabs={tabs}
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                    />
                </Grid>

            </Grid>


            {/* Table */}
            <ReusableTable
                columns={columns}
                data={tableData}
                pagination={true}
                defaultRowsPerPage={10}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
            />

            {/* Confirmation Modal for zero balance */}
            <ConfirmationModal
                open={confirmState.open}
                onCancel={handleConfirmClose}
                onConfirm={onConfirm}
                variant={confirmState.variant}
                title={confirmState.title}
                message={confirmState.message}
                confirmText={confirmState.confirmText}
                cancelText={confirmState.cancelText}
                showCloseIcon={false}
                error={errorApi}
                loading={loadingApi}
            />

        </Box>
    );
};

export default PayoutHistory;